import React, { useState, useRef, useEffect, forwardRef } from 'react'
import { useDraggable, useDroppable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { Modal, Form, Button } from 'react-bootstrap'
import { useAppDispatch, useAppSelector } from '../../../store/index.ts'
import { removeTable, updateTable, unassignGuest, updateGuestStatus, setZoom, setPan, setCanvasView, resetCanvasView } from '../../../store/seatingSlice.ts'
import { updateGuestEndpoint } from '../../../endpoint/index.ts'
import { IGuest, ISeatingPlan, ITable, SeatingSides, TableShape } from '../../../interface/index.ts'
import './index.css'

interface Props {
	seatingPlan: ISeatingPlan | null;
	guests: IGuest[];
	dragTargetPosition?: { x: number; y: number; tableId: string } | null;
	plannerId: string;
	isMobile?: boolean;
	onSeatTap?: (table: ITable, seatIndex: number) => void;
	onAddTable?: (shape: TableShape) => void;
	fabButtonRef?: React.RefObject<HTMLDivElement>;
}

interface TableCardProps {
	table: ITable;
	guests: IGuest[];
	onEdit: (table: ITable) => void;
	onDelete: (tableId: string) => void;
	plannerId: string;
	onEmptySeatClick?: (table: ITable, seatIndex: number) => void;
	onGuestClick?: (guest: IGuest, tableId: string, screenPos: { x: number; y: number }) => void;
}

// Calculate base rectangle seat positions (before any transformations)
const calculateRectangleSeats = (capacity: number, seatingSides: SeatingSides): { x: number; y: number; angle: number }[] => {
	const positions: { x: number; y: number; angle: number }[] = []

	// Rectangle container: 200px × 160px, table rect: x 10-90, y 20-80
	// For 18px gap: horizontal = 18/200 = 9%, vertical = 18/160 = 11%
	// Top: y = 20 - 11 = 9, Bottom: y = 80 + 11 = 91
	// Left: x = 10 - 9 = 1, Right: x = 90 + 9 = 99

	if (seatingSides === 1) {
		// All seats on one long side (bottom - facing up like banquet)
		for (let i = 0; i < capacity; i++) {
			positions.push({
				x: 15 + (i + 0.5) * (70 / capacity),
				y: 91,
				angle: 0
			})
		}
	} else if (seatingSides === 2) {
		// Seats on two long sides (top and bottom)
		const perSide = Math.ceil(capacity / 2)
		// Top side
		for (let i = 0; i < perSide; i++) {
			positions.push({
				x: 15 + (i + 0.5) * (70 / perSide),
				y: 9,
				angle: 180
			})
		}
		// Bottom side
		for (let i = 0; i < capacity - perSide; i++) {
			positions.push({
				x: 15 + (i + 0.5) * (70 / (capacity - perSide)),
				y: 91,
				angle: 0
			})
		}
	} else {
		// 4 sides - distribute along perimeter
		const longSideSeats = Math.ceil(capacity * 0.6) // 60% on long sides
		const shortSideSeats = capacity - longSideSeats
		const topSeats = Math.ceil(longSideSeats / 2)
		const bottomSeats = longSideSeats - topSeats
		const leftSeats = Math.ceil(shortSideSeats / 2)
		const rightSeats = shortSideSeats - leftSeats

		// Top side
		for (let i = 0; i < topSeats; i++) {
			positions.push({
				x: 15 + (i + 0.5) * (70 / topSeats),
				y: 9,
				angle: 180
			})
		}
		// Bottom side
		for (let i = 0; i < bottomSeats; i++) {
			positions.push({
				x: 15 + (i + 0.5) * (70 / bottomSeats),
				y: 91,
				angle: 0
			})
		}
		// Left side
		for (let i = 0; i < leftSeats; i++) {
			positions.push({
				x: 1,
				y: 20 + (i + 0.5) * (60 / leftSeats),
				angle: 90
			})
		}
		// Right side
		for (let i = 0; i < rightSeats; i++) {
			positions.push({
				x: 99,
				y: 20 + (i + 0.5) * (60 / rightSeats),
				angle: -90
			})
		}
	}

	return positions
}

// Calculate seat positions around the table
const getSeatPositions = (
	capacity: number,
	shape: 'circle' | 'rectangle',
	seatingSides: SeatingSides = 4,
	isRotated: boolean = false,
	flipVertical: boolean = false
) => {
	const positions: { x: number; y: number; angle: number }[] = []

	if (shape === 'circle') {
		// Distribute seats evenly around a circle
		// Container is 160px, table circle radius is 38%, we want 18px gap
		// 18px gap = 11.25% of 160px container, so seat radius = 38 + 11 = 49
		for (let i = 0; i < capacity; i++) {
			const angle = (i / capacity) * 2 * Math.PI - Math.PI / 2 // Start from top
			const radius = 49 // Distance from center (38% table radius + 11% for 18px gap)
			positions.push({
				x: 50 + radius * Math.cos(angle),
				y: 50 + radius * Math.sin(angle),
				angle: (angle * 180 / Math.PI) + 90 // Rotate to face center
			})
		}
	} else {
		// Rectangle: get base positions then apply transformations
		let basePositions = calculateRectangleSeats(capacity, seatingSides)

		// Apply vertical flip (mirror seats to opposite sides)
		if (flipVertical) {
			basePositions = basePositions.map(pos => ({
				x: pos.x,
				y: 100 - pos.y, // Mirror around center (y=50)
				angle: (pos.angle + 180) % 360 // Flip facing direction
			}))
		}

		// Apply 90° rotation (swap table orientation)
		if (isRotated) {
			basePositions = basePositions.map(pos => ({
				x: pos.y, // Swap x and y
				y: 100 - pos.x, // Swap and invert for proper rotation
				angle: pos.angle - 90
			}))
		}

		positions.push(...basePositions)
	}

	return positions
}

// Droppable seat component for individual seat drop targeting
interface DroppableSeatProps {
	seatIndex: number;
	table: ITable;
	guest: IGuest | undefined;
	position: { x: number; y: number };
	statusClass: string;
	onSeatClick: (e: React.MouseEvent, guest: IGuest, seatIndex: number, pos: { x: number; y: number }) => void;
	onEmptySeatClick?: (table: ITable, seatIndex: number) => void;
}

// eslint-disable-next-line no-unused-vars
const DroppableSeat: React.FC<DroppableSeatProps> = ({ seatIndex, table, guest, position, statusClass, onSeatClick, onEmptySeatClick }) => {
	const { setNodeRef, isOver } = useDroppable({
		id: `seat-${table.id}-${seatIndex}`,
		data: { type: 'seat', seatIndex, table }
	})

	const handleClick = (e: React.MouseEvent) => {
		e.stopPropagation()
		if (guest) {
			onSeatClick(e, guest, seatIndex, position)
		} else if (onEmptySeatClick) {
			onEmptySeatClick(table, seatIndex)
		}
	}

	return (
		<div
			ref={setNodeRef}
			className={`seat ${guest ? `occupied ${statusClass}` : 'empty'} ${isOver && !guest ? 'drop-target' : ''} ${!guest && onEmptySeatClick ? 'clickable' : ''}`}
			style={{
				left: `${position.x}%`,
				top: `${position.y}%`
			}}
			title={guest ? `${guest.firstName} ${guest.lastName} (${guest.rsvpStatus})` : `Mjesto ${seatIndex + 1} - Klikni za dodavanje gosta`}
			onClick={handleClick}
			onPointerDown={(e) => e.stopPropagation()}
		>
			{guest ? `${guest.firstName.charAt(0)}${guest.lastName.charAt(0)}` : (seatIndex + 1)}
		</div>
	)
}

const TableCard: React.FC<TableCardProps> = ({ table, guests, onEdit, onDelete, onEmptySeatClick, onGuestClick }) => {
	const assignedGuests = guests.filter(g => g.tableId === table.id)
	const [showEditModal, setShowEditModal] = useState(false)
	const [editName, setEditName] = useState(table.name)
	const [editCapacity, setEditCapacity] = useState(table.capacity)
	const [editSeatingSides, setEditSeatingSides] = useState<SeatingSides>(table.seatingSides || 4)
	const [editIsRotated, setEditIsRotated] = useState(table.isRotated || false)
	const [editFlipVertical, setEditFlipVertical] = useState(table.flipVertical || false)

	const { setNodeRef: setDropRef, isOver } = useDroppable({
		id: `table-drop-${table.id}`,
		data: { type: 'table', table }
	})

	const { attributes, listeners, setNodeRef: setDragRef, transform, isDragging } = useDraggable({
		id: `table-drag-${table.id}`,
		data: { type: 'tablePosition', table },
		disabled: false
	})

	const style: React.CSSProperties = {
		left: `${table.position.x}%`,
		top: `${table.position.y}%`,
		transform: transform ? CSS.Translate.toString(transform) : undefined,
		opacity: isDragging ? 0.5 : 1
	}

	const handleDoubleClick = (e: React.MouseEvent) => {
		e.stopPropagation()
		setEditName(table.name)
		setEditCapacity(table.capacity)
		setEditSeatingSides(table.seatingSides || 4)
		setEditIsRotated(table.isRotated || false)
		setEditFlipVertical(table.flipVertical || false)
		setShowEditModal(true)
	}

	const handleSaveEdit = () => {
		if (editName.trim()) {
			onEdit({
				...table,
				name: editName.trim(),
				capacity: editCapacity,
				seatingSides: table.shape === 'rectangle' ? editSeatingSides : undefined,
				isRotated: table.shape === 'rectangle' ? editIsRotated : undefined,
				flipVertical: table.shape === 'rectangle' ? editFlipVertical : undefined
			})
		}
		setShowEditModal(false)
	}

	const handleSeatClick = (e: React.MouseEvent, guest: IGuest, _seatIndex: number, _pos: { x: number; y: number }) => {
		e.stopPropagation()
		if (onGuestClick) {
			const rect = e.currentTarget.getBoundingClientRect()
			onGuestClick(guest, table.id, {
				x: rect.left + rect.width / 2,
				y: rect.top
			})
		}
	}

	const getStatusClass = (status: string) => {
		switch (status) {
			case 'confirmed': return 'status-confirmed'
			case 'declined': return 'status-declined'
			default: return 'status-pending'
		}
	}

	const seatPositions = getSeatPositions(
		table.capacity,
		table.shape,
		table.seatingSides || 4,
		table.isRotated || false,
		table.flipVertical || false
	)

	// Get guest at specific seat position (using stored seatPosition, not array index)
	const getGuestAtSeat = (seatIndex: number): IGuest | undefined => {
		return assignedGuests.find(g => g.seatPosition === seatIndex)
	}

	return (
		<>
			<div
				ref={setDropRef}
				className={`table-card ${table.shape} ${table.isRotated ? 'rotated' : ''} ${isOver ? 'drag-over' : ''} edit-mode ${isDragging ? 'dragging' : ''}`}
				style={style}
				onDoubleClick={handleDoubleClick}
			>
				{/* Table action buttons */}
				<div className='table-action-buttons'>
					<button
						className='table-edit-btn'
						onPointerDown={(e) => {
							e.stopPropagation()
							e.preventDefault()
						}}
						onClick={(e) => {
							e.stopPropagation()
							handleDoubleClick(e)
						}}
						title='Uredi stol'
					>
						✎
					</button>
					<button
						className='table-delete-btn'
						onPointerDown={(e) => {
							e.stopPropagation()
							e.preventDefault()
						}}
						onClick={(e) => {
							e.stopPropagation()
							if (window.confirm(`Obrisati stol "${table.name}"?`)) {
								onDelete(table.id)
							}
						}}
						title='Obriši stol'
					>
						×
					</button>
				</div>

				<div className='table-shape-container'>
					{/* Seats around the table */}
					<div className='table-seats'>
						{seatPositions.map((pos, index) => {
							const guest = getGuestAtSeat(index)
							return (
								<DroppableSeat
									key={index}
									seatIndex={index}
									table={table}
									guest={guest}
									position={pos}
									statusClass={guest ? getStatusClass(guest.rsvpStatus) : ''}
									onSeatClick={handleSeatClick}
									onEmptySeatClick={onEmptySeatClick}
								/>
							)
						})}
					</div>

					{/* Table shape */}
					<div
						ref={setDragRef}
						className='table-shape'
						{...attributes}
						{...listeners}
					>
						{table.shape === 'circle'
							? (
								<svg viewBox='0 0 100 100' className='table-svg'>
									<circle cx='50' cy='50' r='38' />
								</svg>
							)
							: (
								<svg
									viewBox='0 0 100 100'
									className='table-svg'
									style={{
										transform: table.isRotated ? 'rotate(90deg)' : undefined,
										transformOrigin: 'center'
									}}
								>
									<rect x='10' y='20' width='80' height='60' rx='6' />
								</svg>
							)}
					</div>

					{/* Table info in center */}
					<div className='table-info'>
						<span className='table-name'>{table.name}</span>
						<span className='table-count'>{assignedGuests.length}/{table.capacity}</span>
					</div>
				</div>
			</div>

			{/* Edit Modal */}
			<Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered size='sm'>
				<Modal.Header closeButton>
					<Modal.Title>Uredi stol</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<Form.Group className='mb-3'>
						<Form.Label>Ime stola</Form.Label>
						<Form.Control
							type='text'
							value={editName}
							onChange={(e) => setEditName(e.target.value)}
							autoFocus
						/>
					</Form.Group>
					<Form.Group className='mb-3'>
						<Form.Label>Broj mjesta</Form.Label>
						<Form.Control
							type='number'
							min={1}
							max={20}
							value={editCapacity}
							onChange={(e) => setEditCapacity(parseInt(e.target.value) || 1)}
						/>
						{assignedGuests.length > editCapacity && (
							<Form.Text className='text-danger'>
								Upozorenje: Već imate {assignedGuests.length} gostiju za ovaj stol
							</Form.Text>
						)}
					</Form.Group>
					{table.shape === 'rectangle' && (
						<>
							<Form.Group className='mb-3'>
								<Form.Label>Raspored sjedenja</Form.Label>
								<Form.Select
									value={editSeatingSides}
									onChange={(e) => setEditSeatingSides(parseInt(e.target.value) as SeatingSides)}
								>
									<option value={1}>1 strana (banket stil)</option>
									<option value={2}>2 strane (duge strane)</option>
									<option value={4}>4 strane (sve strane)</option>
								</Form.Select>
								<Form.Text className='text-muted'>
									Odaberite s koliko strana stola će sjediti gosti
								</Form.Text>
							</Form.Group>

							<Form.Group className='mb-3'>
								<Form.Label>Orijentacija stola</Form.Label>
								<div className='d-flex gap-2'>
									<Button
										variant={editIsRotated ? 'primary' : 'outline-secondary'}
										size='sm'
										onClick={() => setEditIsRotated(!editIsRotated)}
									>
										↻ Rotiraj 90°
									</Button>
									<Button
										variant={editFlipVertical ? 'primary' : 'outline-secondary'}
										size='sm'
										onClick={() => setEditFlipVertical(!editFlipVertical)}
									>
										↕ Okreni sjedenje
									</Button>
								</div>
								<Form.Text className='text-muted'>
									Rotiraj stol ili prebaci sjedenje na suprotnu stranu
								</Form.Text>
							</Form.Group>
						</>
					)}
				</Modal.Body>
				<Modal.Footer>
					<Button variant='secondary' onClick={() => setShowEditModal(false)}>
						Odustani
					</Button>
					<Button variant='primary' onClick={handleSaveEdit}>
						Spremi
					</Button>
				</Modal.Footer>
			</Modal>
		</>
	)
}

const FloorPlanCanvas = forwardRef<HTMLDivElement, Props>(function FloorPlanCanvas ({ seatingPlan, guests, dragTargetPosition, plannerId, isMobile = false, onSeatTap, onAddTable, fabButtonRef }, ref) {
	const dispatch = useAppDispatch()
	const { canvasView } = useAppSelector(({ seating }) => seating)
	const { zoom, panX, panY } = canvasView

	const containerRef = useRef<HTMLDivElement>(null)
	const [isPanning, setIsPanning] = useState(false)
	const [showFabMenu, setShowFabMenu] = useState(false)
	const panStartRef = useRef({ x: 0, y: 0 })

	// Guest popup state (lifted from TableCard for proper z-index stacking)
	const [guestPopup, setGuestPopup] = useState<{
		guest: IGuest;
		tableId: string;
		position: { x: number; y: number };
	} | null>(null)
	const popupRef = useRef<HTMLDivElement>(null)

	// Touch gesture state refs (using refs to avoid re-renders during gestures)
	const touchStateRef = useRef({
		isPanning: false,
		startX: 0,
		startY: 0,
		lastDistance: null as number | null,
		lastCenterX: 0,
		lastCenterY: 0
	})

	const handleTableEdit = (updatedTable: ITable) => {
		dispatch(updateTable(updatedTable))
	}

	const handleTableDelete = async (tableId: string) => {
		// Find guests assigned to this table and clear their assignment in Firestore
		const affectedGuests = guests.filter(g => g.tableId === tableId)

		await Promise.all(
			affectedGuests.map(guest =>
				updateGuestEndpoint(guest.id, { tableId: null, seatPosition: null }, plannerId)
			)
		)

		dispatch(removeTable(tableId))
	}

	// Guest popup handlers
	const handleGuestClick = (guest: IGuest, tableId: string, screenPos: { x: number; y: number }) => {
		setGuestPopup({ guest, tableId, position: screenPos })
	}

	const handlePopupStatusChange = async (newStatus: 'pending' | 'confirmed' | 'declined') => {
		if (guestPopup) {
			dispatch(updateGuestStatus({ guestId: guestPopup.guest.id, status: newStatus }))
			await updateGuestEndpoint(guestPopup.guest.id, { rsvpStatus: newStatus }, plannerId)
			setGuestPopup(null)
		}
	}

	const handlePopupRemoveGuest = async () => {
		if (guestPopup) {
			dispatch(unassignGuest(guestPopup.guest.id))
			await updateGuestEndpoint(guestPopup.guest.id, { tableId: null, seatPosition: null }, plannerId)
			setGuestPopup(null)
		}
	}

	// Close popup when clicking outside
	useEffect(() => {
		if (!guestPopup) return

		const handleClickOutside = (event: MouseEvent) => {
			if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
				setGuestPopup(null)
			}
		}

		const timeoutId = setTimeout(() => {
			document.addEventListener('click', handleClickOutside)
		}, 0)

		return () => {
			clearTimeout(timeoutId)
			document.removeEventListener('click', handleClickOutside)
		}
	}, [guestPopup])

	// Zoom with mouse wheel (centers on cursor) - use native event for passive: false
	useEffect(() => {
		const container = containerRef.current
		if (!container) return

		const handleWheel = (e: WheelEvent) => {
			e.preventDefault()
			e.stopPropagation()

			const rect = container.getBoundingClientRect()
			const mouseX = e.clientX - rect.left
			const mouseY = e.clientY - rect.top

			const zoomDelta = e.deltaY > 0 ? -0.1 : 0.1
			const newZoom = Math.max(0.25, Math.min(3.0, zoom + zoomDelta))

			// Adjust pan to keep cursor position fixed
			const scale = newZoom / zoom
			const newPanX = mouseX - (mouseX - panX) * scale
			const newPanY = mouseY - (mouseY - panY) * scale

			dispatch(setZoom(newZoom))
			dispatch(setPan({ x: newPanX, y: newPanY }))
		}

		container.addEventListener('wheel', handleWheel, { passive: false })
		return () => container.removeEventListener('wheel', handleWheel)
	}, [zoom, panX, panY, dispatch])

	// Center canvas on initial mount (when at default 0.45 zoom with pan at 0,0)
	useEffect(() => {
		const container = containerRef.current
		if (!container) return

		// Only center if at initial state (zoom near 0.45 and pan at 0)
		if (Math.abs(zoom - 0.45) < 0.01 && panX === 0 && panY === 0) {
			const rect = container.getBoundingClientRect()
			const scaledWidth = 3000 * zoom
			const scaledHeight = 2000 * zoom

			const centerX = (rect.width - scaledWidth) / 2
			const centerY = (rect.height - scaledHeight) / 2

			dispatch(setPan({ x: centerX, y: centerY }))
		}
	}, []) // Run only on mount

	// Pan with mouse drag (only on empty area)
	const handleMouseDown = (e: React.MouseEvent) => {
		// Only pan if clicking empty area (not a table or control)
		const target = e.target as HTMLElement
		if (target.closest('.table-card') || target.closest('.canvas-controls')) return

		setIsPanning(true)
		panStartRef.current = { x: e.clientX - panX, y: e.clientY - panY }
	}

	const handleMouseMove = (e: React.MouseEvent) => {
		if (!isPanning) return
		dispatch(setPan({
			x: e.clientX - panStartRef.current.x,
			y: e.clientY - panStartRef.current.y
		}))
	}

	const handleMouseUp = () => {
		setIsPanning(false)
	}

	const handleMouseLeave = () => {
		setIsPanning(false)
	}

	// Touch gesture helpers
	const getDistance = (touches: React.TouchList) => {
		const dx = touches[0].clientX - touches[1].clientX
		const dy = touches[0].clientY - touches[1].clientY
		return Math.sqrt(dx * dx + dy * dy)
	}

	const getCenter = (touches: React.TouchList, rect: DOMRect) => ({
		x: (touches[0].clientX + touches[1].clientX) / 2 - rect.left,
		y: (touches[0].clientY + touches[1].clientY) / 2 - rect.top
	})

	// Touch pan and pinch-to-zoom handlers
	const handleTouchStart = (e: React.TouchEvent) => {
		const target = e.target as HTMLElement
		// Don't interfere with table or control interactions
		if (target.closest('.table-card') || target.closest('.canvas-controls') || target.closest('.add-table-fab')) return

		const touch = touchStateRef.current

		if (e.touches.length === 1) {
			// Single finger - start panning
			touch.isPanning = true
			touch.startX = e.touches[0].clientX - panX
			touch.startY = e.touches[0].clientY - panY
			touch.lastDistance = null
			setIsPanning(true)
		} else if (e.touches.length === 2) {
			// Two fingers - start pinch-to-zoom
			touch.isPanning = false
			touch.lastDistance = getDistance(e.touches)
			const rect = containerRef.current?.getBoundingClientRect()
			if (rect) {
				const center = getCenter(e.touches, rect)
				touch.lastCenterX = center.x
				touch.lastCenterY = center.y
			}
			setIsPanning(false)
		}
	}

	const handleTouchMove = (e: React.TouchEvent) => {
		const touch = touchStateRef.current

		if (e.touches.length === 1 && touch.isPanning) {
			// Single finger panning
			dispatch(setPan({
				x: e.touches[0].clientX - touch.startX,
				y: e.touches[0].clientY - touch.startY
			}))
		} else if (e.touches.length === 2 && touch.lastDistance !== null) {
			// Pinch-to-zoom
			const newDistance = getDistance(e.touches)
			const rect = containerRef.current?.getBoundingClientRect()
			if (!rect) return

			const center = getCenter(e.touches, rect)
			const scale = newDistance / touch.lastDistance
			const newZoom = Math.max(0.25, Math.min(3.0, zoom * scale))

			// Adjust pan to keep pinch center fixed
			const zoomRatio = newZoom / zoom
			const newPanX = center.x - (center.x - panX) * zoomRatio
			const newPanY = center.y - (center.y - panY) * zoomRatio

			dispatch(setZoom(newZoom))
			dispatch(setPan({ x: newPanX, y: newPanY }))

			touch.lastDistance = newDistance
			touch.lastCenterX = center.x
			touch.lastCenterY = center.y
		}
	}

	const handleTouchEnd = () => {
		const touch = touchStateRef.current
		touch.isPanning = false
		touch.lastDistance = null
		setIsPanning(false)
	}

	// Zoom controls
	const handleZoomIn = () => {
		const newZoom = Math.min(3.0, zoom + 0.25)
		dispatch(setZoom(newZoom))
	}

	const handleZoomOut = () => {
		const newZoom = Math.max(0.25, zoom - 0.25)
		dispatch(setZoom(newZoom))
	}

	const handleResetView = () => {
		const container = containerRef.current
		if (!container) {
			dispatch(resetCanvasView())
			return
		}

		const rect = container.getBoundingClientRect()
		const newZoom = 0.45
		const scaledWidth = 3000 * newZoom
		const scaledHeight = 2000 * newZoom

		const centerX = (rect.width - scaledWidth) / 2
		const centerY = (rect.height - scaledHeight) / 2

		dispatch(setCanvasView({ zoom: newZoom, panX: centerX, panY: centerY }))
	}

	// Find the table being dragged to get its shape
	const draggedTable = dragTargetPosition
		? seatingPlan?.tables.find(t => t.id === dragTargetPosition.tableId)
		: null

	if (!seatingPlan) {
		return (
			<div className='floor-plan-canvas'>
				<div className='floor-plan-loading'>
					Učitavanje rasporeda...
				</div>
			</div>
		)
	}

	// Combine forwarded ref with local ref
	const setRefs = (element: HTMLDivElement | null) => {
		// Set local ref
		(containerRef as React.MutableRefObject<HTMLDivElement | null>).current = element
		// Set forwarded ref
		if (typeof ref === 'function') {
			ref(element)
		} else if (ref) {
			ref.current = element
		}
	}

	return (
		<div
			className={`floor-plan-canvas ${isMobile ? 'mobile' : ''}`}
			ref={setRefs}
			onMouseDown={handleMouseDown}
			onMouseMove={handleMouseMove}
			onMouseUp={handleMouseUp}
			onMouseLeave={handleMouseLeave}
			onTouchStart={handleTouchStart}
			onTouchMove={handleTouchMove}
			onTouchEnd={handleTouchEnd}
		>
			{/* Zoom controls */}
			<div className='canvas-controls'>
				<button
					className='canvas-control-btn'
					onClick={handleZoomOut}
					disabled={zoom <= 0.25}
					title='Umanji'
				>
					−
				</button>
				<span className='zoom-level'>{Math.round(zoom * 100)}%</span>
				<button
					className='canvas-control-btn'
					onClick={handleZoomIn}
					disabled={zoom >= 3.0}
					title='Uvećaj'
				>
					+
				</button>
				<button
					className='canvas-control-btn reset-btn'
					onClick={handleResetView}
					title='Resetiraj prikaz'
				>
					⟲
				</button>
			</div>

			{/* Transform container for pan/zoom */}
			<div
				className={`floor-plan-transform ${isPanning ? 'panning' : ''}`}
				style={{
					transform: `translate(${panX}px, ${panY}px) scale(${zoom})`,
					transformOrigin: '0 0'
				}}
			>
				<div className='floor-plan-area'>
					{seatingPlan.tables.length === 0
						? (
							<div className='floor-plan-empty'>
								<p>Nema stolova.</p>
								<p>Koristite alatnu traku iznad za dodavanje stolova.</p>
							</div>
						)
						: (
							seatingPlan.tables.map(table => (
								<TableCard
									key={table.id}
									table={table}
									guests={guests}
									onEdit={handleTableEdit}
									onDelete={handleTableDelete}
									plannerId={plannerId}
									onGuestClick={handleGuestClick}
									onEmptySeatClick={onSeatTap}
								/>
							))
						)}

					{/* Drag target indicator - shows where table will land */}
					{dragTargetPosition && draggedTable && (
						<div
							className={`table-drop-indicator ${draggedTable.shape}`}
							style={{
								left: `${dragTargetPosition.x}%`,
								top: `${dragTargetPosition.y}%`
							}}
						>
							<div className='drop-indicator-inner' />
						</div>
					)}
				</div>
			</div>

			{/* FAB for adding tables on mobile */}
			{isMobile && onAddTable && (
				<div className='add-table-fab' ref={fabButtonRef}>
					<button
						className='fab-btn'
						onClick={() => setShowFabMenu(!showFabMenu)}
						aria-label='Dodaj stol'
					>
						+
					</button>
					{showFabMenu && (
						<>
							<div className='fab-menu-backdrop' onClick={() => setShowFabMenu(false)} />
							<div className='fab-menu'>
								<button
									className='fab-menu-item'
									onClick={() => {
										onAddTable('circle')
										setShowFabMenu(false)
									}}
								>
									<svg viewBox='0 0 24 24' width='20' height='20'>
										<circle cx='12' cy='12' r='10' fill='none' stroke='currentColor' strokeWidth='2' />
									</svg>
									Okrugli stol
								</button>
								<button
									className='fab-menu-item'
									onClick={() => {
										onAddTable('rectangle')
										setShowFabMenu(false)
									}}
								>
									<svg viewBox='0 0 24 24' width='20' height='20'>
										<rect x='2' y='6' width='20' height='12' fill='none' stroke='currentColor' strokeWidth='2' rx='2' />
									</svg>
									Pravokutni stol
								</button>
							</div>
						</>
					)}
				</div>
			)}

			{/* Guest popup - rendered outside transform for proper z-index stacking */}
			{guestPopup && (() => {
				// Flip popup below seat if near top of viewport (popup is ~200px tall)
				const shouldFlip = guestPopup.position.y < 200
				return (
					<div
						ref={popupRef}
						className={`guest-popup ${shouldFlip ? 'flipped' : ''}`}
						style={{
							position: 'fixed',
							left: guestPopup.position.x,
							top: guestPopup.position.y,
							transform: shouldFlip
								? 'translate(-50%, 10px)'
								: 'translate(-50%, -100%)',
							marginTop: shouldFlip ? '0' : '-10px'
						}}
						onClick={(e) => e.stopPropagation()}
						onPointerDown={(e) => e.stopPropagation()}
					>
						<div className='guest-popup-header'>
							<span>{guestPopup.guest.firstName} {guestPopup.guest.lastName}</span>
							<button className='guest-popup-close' onClick={() => setGuestPopup(null)}>×</button>
						</div>
						<div className='guest-popup-status'>
							<span>Status:</span>
							<div className='status-buttons'>
								<button
									className={`status-btn confirmed ${guestPopup.guest.rsvpStatus === 'confirmed' ? 'active' : ''}`}
									onClick={() => handlePopupStatusChange('confirmed')}
								>
									Potvrđeno
								</button>
								<button
									className={`status-btn pending ${guestPopup.guest.rsvpStatus === 'pending' ? 'active' : ''}`}
									onClick={() => handlePopupStatusChange('pending')}
								>
									Na čekanju
								</button>
								<button
									className={`status-btn declined ${guestPopup.guest.rsvpStatus === 'declined' ? 'active' : ''}`}
									onClick={() => handlePopupStatusChange('declined')}
								>
									Odbijeno
								</button>
							</div>
						</div>
						<button className='guest-popup-remove' onClick={handlePopupRemoveGuest}>
							Ukloni sa stola
						</button>
					</div>
				)
			})()}

		</div>
	)
})

export default FloorPlanCanvas
